import { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';
import { ShieldCheck, RefreshCw } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_CFG = {
  NOT_CHECKED: 'bg-slate-100 text-slate-600',
  PENDING: 'bg-blue-100 text-blue-700',
  PASS: 'bg-emerald-100 text-emerald-700',
  FAIL: 'bg-red-100 text-red-700',
  REVIEW: 'bg-amber-100 text-amber-800',
  ERROR: 'bg-orange-100 text-orange-700',
};

const YesNo = ({ v }) => v == null ? <span className="text-slate-400">—</span> :
  <span className={String(v).toLowerCase() === 'yes' ? 'text-emerald-700 font-semibold' : 'text-red-600 font-semibold'}>{String(v)}</span>;

const SA_BANKS = ['ABSA', 'Capitec', 'FNB', 'Nedbank', 'Standard Bank', 'African Bank', 'TymeBank', 'Discovery Bank', 'Investec', 'Other'];
const BANK_BRANCH_CODES = {
  'ABSA': '632005', 'Capitec': '470010', 'FNB': '250655', 'Nedbank': '198765', 'Standard Bank': '051001',
  'African Bank': '430000', 'TymeBank': '678910', 'Discovery Bank': '679000', 'Investec': '580105',
};

export const VerifyNowPanel = ({ appId, app, getAuthHeader, onEdited }) => {
  const [data, setData] = useState(null);
  const [running, setRunning] = useState(false);
  const [branch, setBranch] = useState('');
  const [editing, setEditing] = useState(false);
  const [edit, setEdit] = useState({});
  const [savingEdit, setSavingEdit] = useState(false);

  const openEdit = () => {
    setEdit({
      id_number: app?.id_number || '', bank_name: app?.bank_name || '',
      account_number: app?.account_number || '', branch_code: app?.branch_code || '',
      account_type: app?.account_type || '',
    });
    setEditing(true);
  };

  const saveEdit = async () => {
    setSavingEdit(true);
    try {
      await axios.put(`${API_URL}/admin/customer-applications/${appId}/banking`, edit, getAuthHeader());
      toast.success('Details updated — previous verification for changed details is reset');
      setEditing(false);
      if (app) Object.assign(app, Object.fromEntries(Object.entries(edit).filter(([, v]) => String(v).trim())));
      load();
      onEdited && onEdited();
    } catch (e) { toast.error(e.response?.data?.detail || 'Update failed'); }
    setSavingEdit(false);
  };

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/admin/customer-applications/${appId}/verification`, getAuthHeader());
      setData(res.data);
      setBranch(res.data.branch_code || '');
    } catch { /* ignore */ }
  }, [appId, getAuthHeader]);

  useEffect(() => { load(); }, [load]);

  const run = async () => {
    if (!window.confirm('Run VerifyNow ID verification and bank account ownership check?\n\nConfirm: the applicant has received the required privacy notice and authorised these checks. Paid checks are only repeated when details changed.')) return;
    setRunning(true);
    try {
      const body = { consent: true };
      if (branch && branch !== data?.branch_code) body.branch_code = branch;
      const res = await axios.post(`${API_URL}/admin/customer-applications/${appId}/verify`, body, getAuthHeader());
      toast.success(`ID: ${res.data.id.status}${res.data.bank ? ` · Bank: ${res.data.bank.status}` : ''}`);
      if (res.data.note) toast.info(res.data.note);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Verification failed'); }
    setRunning(false);
  };

  if (!data) return null;
  const rows = [
    { label: 'ID Verification', v: data.id },
    { label: 'Bank Ownership', v: data.bank },
  ];
  return (
    <div className="p-3 bg-indigo-50/40 border border-indigo-100 rounded-lg space-y-2" data-testid="verifynow-panel">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs font-bold text-indigo-900 flex items-center gap-1.5">
          <ShieldCheck className="w-4 h-4" />VerifyNow — ID & Bank Ownership
          <Badge variant="outline" className={`text-[10px] border-0 ${data.mode === 'production' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-800'}`} data-testid="verifynow-mode">
            {data.mode.toUpperCase()}
          </Badge>
          {data.mode !== 'production' && <span className="text-[10px] text-amber-700 font-normal">sandbox results never qualify a live payout</span>}
        </p>
        <div className="flex items-center gap-2">
          <Input value={branch} onChange={e => setBranch(e.target.value.replace(/\D/g, '').slice(0, 6))} placeholder="Branch code"
            className="h-7 w-[110px] text-xs" data-testid="verifynow-branch-input" />
          <Button size="sm" variant="outline" className="h-7 text-xs border-indigo-200 text-indigo-700" onClick={() => editing ? setEditing(false) : openEdit()} data-testid="verifynow-edit-details-btn">
            {editing ? 'Cancel' : 'Edit Details'}
          </Button>
          <Button size="sm" className="h-7 bg-indigo-600 hover:bg-indigo-700 text-xs" onClick={run} disabled={running} data-testid="verify-id-bank-btn">
            {running ? 'Verifying…' : 'Verify ID & Bank Account'}
          </Button>
          <Button size="sm" variant="ghost" className="h-7 px-2" onClick={load} data-testid="verifynow-refresh"><RefreshCw className="w-3.5 h-3.5" /></Button>
        </div>
      </div>
      {editing && (
        <div className="bg-white/80 rounded-md border border-indigo-200 p-2 space-y-2" data-testid="verifynow-edit-form">
          <p className="text-[11px] font-semibold text-indigo-800">Correct ID / bank details (any change resets that verification)</p>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
            <div>
              <p className="text-[10px] text-slate-500">ID Number</p>
              <Input value={edit.id_number} onChange={e => setEdit(f => ({ ...f, id_number: e.target.value.replace(/\D/g, '').slice(0, 13) }))} className="h-7 text-xs" data-testid="vn-edit-id-number" />
            </div>
            <div>
              <p className="text-[10px] text-slate-500">Bank</p>
              <select value={edit.bank_name} onChange={e => {
                const v = e.target.value;
                setEdit(f => ({ ...f, bank_name: v, branch_code: BANK_BRANCH_CODES[v] || f.branch_code }));
              }} className="h-7 w-full text-xs rounded-md border border-input bg-background px-2" data-testid="vn-edit-bank">
                <option value="">Select…</option>
                {SA_BANKS.map(b => <option key={b} value={b}>{b}{BANK_BRANCH_CODES[b] ? ` (${BANK_BRANCH_CODES[b]})` : ''}</option>)}
              </select>
            </div>
            <div>
              <p className="text-[10px] text-slate-500">Account No</p>
              <Input value={edit.account_number} onChange={e => setEdit(f => ({ ...f, account_number: e.target.value.replace(/\D/g, '').slice(0, 15) }))} className="h-7 text-xs" data-testid="vn-edit-account" />
            </div>
            <div>
              <p className="text-[10px] text-slate-500">Branch Code</p>
              <Input value={edit.branch_code} onChange={e => setEdit(f => ({ ...f, branch_code: e.target.value.replace(/\D/g, '').slice(0, 6) }))} className="h-7 text-xs" data-testid="vn-edit-branch" />
            </div>
            <div>
              <p className="text-[10px] text-slate-500">Account Type</p>
              <select value={edit.account_type} onChange={e => setEdit(f => ({ ...f, account_type: e.target.value }))} className="h-7 w-full text-xs rounded-md border border-input bg-background px-2" data-testid="vn-edit-acctype">
                <option value="">Select…</option>
                {['savings', 'cheque', 'current', 'transmission'].map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
          </div>
          <Button size="sm" className="h-7 bg-indigo-700 hover:bg-indigo-800 text-xs" onClick={saveEdit} disabled={savingEdit} data-testid="vn-save-details-btn">
            {savingEdit ? 'Saving…' : 'Save Details'}
          </Button>
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {rows.map(({ label, v }) => (
          <div key={label} className="bg-white/70 rounded-md border border-indigo-100 p-2" data-testid={`verifynow-${label === 'ID Verification' ? 'id' : 'bank'}-status`}>
            <div className="flex items-center justify-between">
              <p className="text-[11px] font-semibold text-slate-600">{label}</p>
              <Badge variant="outline" className={`border-0 text-[10px] ${STATUS_CFG[v?.status] || STATUS_CFG.NOT_CHECKED}`}>{v?.status || 'NOT_CHECKED'}</Badge>
            </div>
            {v?.checked_at && <p className="text-[10px] text-slate-400">checked {new Date(v.checked_at).toLocaleString('en-ZA')} · {v.mode}</p>}
            {v?.reason && <p className="text-[10px] text-slate-600 mt-0.5">{v.reason}</p>}
            {label === 'Bank Ownership' && (
              <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[10px] text-slate-500 mt-1">
                <span>{data.bank_name || '—'} · {data.masked_account} · {data.account_type}</span>
                <span>Open: <YesNo v={v?.account_open} /></span>
                <span>Credits: <YesNo v={v?.accepts_credits} /></span>
                <span>Debits: <YesNo v={v?.accepts_debits} /></span>
                <span>ID match: <YesNo v={v?.identity_match} /></span>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
